// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button, { ButtonPresentationProps } from "@ces/MobiusOverrides/Button/Button";
import { mergeWithCss } from "@ces/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";

interface OwnProps extends WidgetProps {
    fields: {
        url: LinkFieldValue;
        openInNewTab: boolean;
    };
    expandedStyles?: JobTrackStyles;
}

export interface JobTrackStyles {
    wrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    changeLink?: ButtonPresentationProps;
}

export const jobTrackStyles: JobTrackStyles = {};

const JobTrack = ({ expandedStyles, fields }: OwnProps) => {
    const styles = mergeWithCss(jobTrackStyles, expandedStyles);
    const buttonLink = useGetLink(fields.url).url || "";
    const history = useHistory();

    const handleClick = (url: string) => {
        if (!url) {
            return;
        }
        if (fields.openInNewTab) {
            window.open(url, "_blank");
        } else if (url.startsWith("http")) {
            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    return (
        <StyledWrapper id="JobTrackStyles" role="region" aria-live="polite" {...styles.wrapper}>
            <Button onClick={() => handleClick(buttonLink)} {...styles.changeLink}>
                {translate("Job Track")}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: JobTrack,
    definition: {
        group: "Header",
        icon: "store",
        fieldDefinitions: [
            {
                name: "url",
                editorTemplate: "LinkField",
                defaultValue: {
                    type: "Url",
                    value: "",
                },
                fieldType: "General",
            },
            {
                name: "openInNewTab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
